import { compareInstantsAtMicrosecondPrecision, normalizeInstantToUtcMicroseconds } from "../lib/temporalInstant";
import { apiClient, getAuthToken } from "./client";
import {
  ExportIntegrityError,
  exportBlob,
  hasExactMediaType,
  readBoundedResponseBytes,
  verifyJsonExportBytes,
  verifyOfflineHtmlBytes,
} from "./exportIntegrity";

export const TEMPORAL_COMPARE_CONTRACT_VERSION = "thread.lineage.temporal-compare.v1" as const;
export const TEMPORAL_COMPARE_RESOURCE_LIMITS_VERSION = "thread.lineage.temporal-compare-resource-limits.v1" as const;
export const TEMPORAL_COMPARE_JSON_MEDIA_TYPE = "application/vnd.thread.lineage-temporal-compare.v1+json" as const;
export const TEMPORAL_COMPARE_PATH = "/thread/v1/provenance/lineage/compare" as const;
export const TEMPORAL_COMPARE_EXPORT_PATH = "/thread/v1/provenance/lineage/compare/export" as const;
export const TEMPORAL_COMPARE_MIN_SCHEMA_MIGRATION = "20260810210000" as const;

export const TEMPORAL_COMPARE_DEFAULT_LIMITS = {
  max_depth: 8,
  max_records: 200,
  max_paths: 1000,
  max_path_steps: 10_000,
} as const;

export const TEMPORAL_COMPARE_MAX_DOCUMENT_CANONICAL_BYTES = 4_194_304;
const TEMPORAL_COMPARE_MAX_HTML_BYTES = 33_554_432;
const TEMPORAL_COMPARE_MAX_ENVELOPE_CANONICAL_BYTES = TEMPORAL_COMPARE_MAX_DOCUMENT_CANONICAL_BYTES + 65_536;
const TEMPORAL_COMPARE_MAX_ADMISSION_DEPTH = 64;
const TEMPORAL_COMPARE_MAX_ADMISSION_NODES = 1_000_000;

export type TemporalCompareDirection = "upstream" | "downstream" | "both";
export type TemporalComparePathDirection = "self" | "upstream" | "downstream";
export type TemporalCompareSide = "before" | "after";
export type TemporalCompareDecisionStatus =
  | "proposed"
  | "ratified"
  | "under_review"
  | "pending_ratification"
  | "withdrawn"
  | "superseded";

export interface TemporalCompareRoot {
  entity_kind: "artifact_version";
  id: string;
}

export interface TemporalCompareRequest {
  project_id: string;
  root_refs: TemporalCompareRoot[];
  before_as_of: string;
  after_as_of: string;
  recorded_as_of?: string | null;
  snapshot_token?: string | null;
  direction?: TemporalCompareDirection;
  max_depth?: number;
  max_records?: number;
  max_paths?: number;
  max_path_steps?: number;
}

/** Exact replay selector used by export; defaults are materialized deliberately. */
export interface TemporalCompareExportRequest {
  project_id: string;
  root_refs: TemporalCompareRoot[];
  before_as_of: string;
  after_as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  direction: TemporalCompareDirection;
  max_depth: number;
  max_records: number;
  max_paths: number;
  max_path_steps: number;
  expected_semantic_digest: string;
}

export interface TemporalCompareLimits {
  max_depth: number;
  max_records: number;
  max_paths: number;
  max_path_steps: number;
  max_causal_contexts: 10000;
}

export interface TemporalCompareScope {
  root_refs: TemporalCompareRoot[];
  direction: TemporalCompareDirection;
  limits: TemporalCompareLimits;
  resource_limits_version: typeof TEMPORAL_COMPARE_RESOURCE_LIMITS_VERSION;
}

export interface TemporalCompareSummary {
  topology_added: number;
  topology_removed: number;
  supersessions_added: number;
  validity_changed: number;
  decision_status_changed: number;
  evidence_added: number;
  evidence_removed: number;
}

interface TemporalCitationBase {
  citation_id: string;
  record_id: string;
  record_ordinal: number | null;
  effective_at: string;
  recorded_at: string;
}

export interface TemporalArtifactVersionCitation extends TemporalCitationBase {
  record_kind: "artifact_version";
  artifact_version_id: string;
  initial_lifecycle_state: "active";
}

export interface TemporalOperationRunCitation extends TemporalCitationBase {
  record_kind: "operation_run";
  operation_id: string;
  status: "completed";
}

export interface TemporalBindingCitation extends TemporalCitationBase {
  record_kind: "binding";
  binding_id: string;
  operation_id: string;
  artifact_version_id: string;
  direction: "input" | "output";
  role: string;
  ordinal: number;
  propagates_invalidity: boolean;
}

export interface TemporalSupersessionCitation extends TemporalCitationBase {
  record_kind: "supersession";
  supersession_id: string;
  old_artifact_version_id: string;
  new_artifact_version_id: string;
  change_type: string;
}

export interface TemporalLifecycleCitation extends TemporalCitationBase {
  record_kind: "lifecycle_event";
  lifecycle_event_id: string;
  artifact_version_id: string;
  event_type: "invalidated" | "superseded" | "redacted" | "abandoned" | "declared_inapplicable" | "reactivated";
  state: "active" | "invalidated" | "superseded" | "redacted" | "abandoned" | "inapplicable";
}

export interface TemporalDecisionCitation extends TemporalCitationBase {
  record_kind: "decision";
  decision_id: string;
  artifact_version_id: string;
}

export interface TemporalDecisionEvidenceCitation extends TemporalCitationBase {
  record_kind: "decision_evidence";
  decision_id: string;
  artifact_version_id: string;
  ordinal: number;
}

export interface TemporalDecisionStatusCitation extends TemporalCitationBase {
  record_kind: "decision_status_event";
  decision_status_event_id: string;
  decision_id: string;
  artifact_version_id: string;
  status: TemporalCompareDecisionStatus;
}

export interface TemporalTopologyRetirementCitation extends TemporalCitationBase {
  record_kind: "topology_retirement";
  retirement_event_id: string;
  retired_entity_kind: "artifact" | "artifact_version" | "operation_run" | "binding" | "supersession" | "merge_input" | "decision_evidence";
  retired_entity_id: string;
  retired_entity_ordinal: number | null;
  retirement_type: "retired" | "withdrawn" | "superseded" | "merged_out";
}

export type TemporalCompareCitation =
  | TemporalArtifactVersionCitation
  | TemporalOperationRunCitation
  | TemporalBindingCitation
  | TemporalSupersessionCitation
  | TemporalLifecycleCitation
  | TemporalDecisionCitation
  | TemporalDecisionEvidenceCitation
  | TemporalDecisionStatusCitation
  | TemporalTopologyRetirementCitation;

export interface TemporalOperationPathStep {
  step_kind: "operation";
  ordinal: number;
  from_artifact_version_id: string;
  operation_id: string;
  to_artifact_version_id: string;
  input_binding_id: string;
  input_role: string;
  output_binding_id: string;
  output_role: string;
  input_propagates_invalidity: boolean;
  input_binding_citation_id: string;
  operation_run_citation_id: string;
  output_binding_citation_id: string;
}

export interface TemporalSupersessionPathStep {
  step_kind: "supersession";
  ordinal: number;
  old_artifact_version_id: string;
  new_artifact_version_id: string;
  supersession_id: string;
  change_type: string;
  supersession_citation_id: string;
}

export type TemporalComparePathStep = TemporalOperationPathStep | TemporalSupersessionPathStep;

export interface TemporalComparePath {
  path_id: string;
  root_ref: TemporalCompareRoot;
  temporal_side: TemporalCompareSide;
  direction: TemporalComparePathDirection;
  depth: number;
  steps: TemporalComparePathStep[];
}

export interface TemporalCausalContext {
  path_id: string;
  root_ref: TemporalCompareRoot;
  temporal_side: TemporalCompareSide;
  direction: TemporalComparePathDirection;
  depth: number;
}

interface TemporalTopologyEdge {
  from_artifact_version_id: string;
  operation_id: string;
  to_artifact_version_id: string;
  input_binding_id: string;
  input_role: string;
  output_binding_id: string;
  output_role: string;
  input_propagates_invalidity: boolean;
}

export interface TemporalTopologyAddition extends TemporalTopologyEdge {
  causal_contexts: TemporalCausalContext[];
  input_binding_citation_id: string;
  operation_run_citation_id: string;
  output_binding_citation_id: string;
}

export interface TemporalTopologyRemoval extends TemporalTopologyEdge {
  causal_contexts: TemporalCausalContext[];
  prior_input_binding_citation_id: string;
  prior_operation_run_citation_id: string;
  prior_output_binding_citation_id: string;
  retirement_citation_id: string;
  removal_category: "binding" | "operation_run";
  retirement_action: "retired" | "withdrawn" | "superseded" | "merged_out";
}

export interface TemporalSupersessionAddition {
  causal_contexts: TemporalCausalContext[];
  supersession_id: string;
  old_artifact_version_id: string;
  new_artifact_version_id: string;
  change_type: string;
  evidence_citation_id: string;
}

export interface TemporalValidityChange {
  causal_contexts: TemporalCausalContext[];
  artifact_version_id: string;
  before_state: string;
  after_state: string;
  considered_valid_before: boolean;
  considered_valid_after: boolean;
  before_citation_id: string;
  after_citation_id: string;
  timeline_citation_ids: string[];
  timeline_complete: true;
}

export interface TemporalDecisionStatusChange {
  causal_contexts: TemporalCausalContext[];
  decision_id: string;
  before_status: TemporalCompareDecisionStatus;
  after_status: TemporalCompareDecisionStatus;
  before_citation_id: string;
  after_citation_id: string;
  timeline_citation_ids: string[];
  timeline_complete: true;
}

export interface TemporalEvidenceMembershipAddition {
  causal_contexts: TemporalCausalContext[];
  decision_id: string;
  artifact_version_id: string;
  ordinal: number;
  evidence_citation_id: string;
}

export interface TemporalEvidenceMembershipRemoval {
  causal_contexts: TemporalCausalContext[];
  decision_id: string;
  artifact_version_id: string;
  ordinal: number;
  prior_evidence_citation_id: string;
  retirement_citation_id: string;
  removal_category: "decision_evidence";
  retirement_action: "retired" | "withdrawn" | "superseded" | "merged_out";
}

export type TemporalCompareGapCode =
  | "bounded_projection"
  | "effective_time_unavailable"
  | "incomplete_recorded_history"
  | "typed_retirement_evidence_unavailable"
  | "unknown_record_type"
  | "unknown_relationship_type"
  | "unsupported_semantics";

export interface TemporalEntityRef {
  entity_kind:
    | "artifact_version"
    | "operation_run"
    | "binding"
    | "supersession"
    | "lifecycle_event"
    | "decision"
    | "decision_evidence"
    | "decision_status_event"
    | "binding_state_event"
    | "operation_state_event"
    | "topology_retirement";
  id: string;
}

export interface TemporalCompareGap {
  code: TemporalCompareGapCode;
  marker:
    | "bounded_projection_no_claim"
    | "effective_time_unavailable_no_claim"
    | "temporal_history_incomplete_no_subject"
    | "typed_retirement_evidence_unavailable_no_claim"
    | "unknown_record_type_no_claim"
    | "unknown_relationship_type_no_claim"
    | "unsupported_semantics_no_claim";
  subject: TemporalEntityRef | null;
}

export interface TemporalCompletenessState {
  status: "complete" | "incomplete";
  gaps: TemporalCompareGap[];
}

export interface TemporalCompareSideCompleteness extends TemporalCompletenessState {
  effective_as_of: string;
}

export interface TemporalCompareDocument {
  contract_version: typeof TEMPORAL_COMPARE_CONTRACT_VERSION;
  project_id: string;
  before_as_of: string;
  after_as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  source_contract_version: "thread.lineage.v2";
  temporal_basis: "effective_and_recorded_time";
  scope: TemporalCompareScope;
  summary: TemporalCompareSummary;
  paths: TemporalComparePath[];
  topology: { added: TemporalTopologyAddition[]; removed: TemporalTopologyRemoval[] };
  supersessions_added: TemporalSupersessionAddition[];
  validity_changes: TemporalValidityChange[];
  decision_status_changes: TemporalDecisionStatusChange[];
  evidence_membership: {
    added: TemporalEvidenceMembershipAddition[];
    removed: TemporalEvidenceMembershipRemoval[];
  };
  citations: TemporalCompareCitation[];
  completeness: {
    before: TemporalCompareSideCompleteness;
    after: TemporalCompareSideCompleteness;
    comparison: TemporalCompletenessState;
  };
}

export interface TemporalCompareSemanticIdentity {
  digest: string;
  canonicalizer: "rfc8785";
  canonicalizer_version: "1";
  byte_length: number;
  media_type: typeof TEMPORAL_COMPARE_JSON_MEDIA_TYPE;
}

export interface TemporalCompareResult {
  document: TemporalCompareDocument;
  semantic_identity: TemporalCompareSemanticIdentity;
}

export interface TemporalCompareEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: TemporalCompareResult;
  page: null;
  provenance: {
    source: "thread_core";
    projection_version: typeof TEMPORAL_COMPARE_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: [];
}

export type TemporalCompareExportFormat = "json" | "html";

export interface TemporalCompareExport {
  blob: Blob;
  filename: string;
  mediaType: string;
  semanticDigest: string;
}

type UnknownRecord = Record<string, unknown>;

const REQUEST_UUID_PATTERN = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const CITATION_ID_PATTERN = /^tc:[a-z0-9_]+:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?::(?:0|[1-9][0-9]*))?$/;
const DIGEST_PATTERN = /^sha256:[0-9a-f]{64}$/;
const SNAPSHOT_TOKEN_PATTERN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;
const FIXED_UTC_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d\.\d{6}Z$/;
const AWARE_TIMESTAMP_PATTERN = /^(?:(?:0001|9999)-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?Z|(?!(?:0000|0001|9999)-)\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d))$/;
const TIMESTAMP_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|([+-])(\d{2}):(\d{2}))$/;

const DECISION_STATUSES = ["proposed", "ratified", "under_review", "pending_ratification", "withdrawn", "superseded"] as const;
const RETIREMENT_ACTIONS = ["retired", "withdrawn", "superseded", "merged_out"] as const;
const GAP_CODES = ["bounded_projection", "effective_time_unavailable", "incomplete_recorded_history", "typed_retirement_evidence_unavailable", "unknown_record_type", "unknown_relationship_type", "unsupported_semantics"] as const;
const GAP_MARKERS = {
  bounded_projection: "bounded_projection_no_claim",
  effective_time_unavailable: "effective_time_unavailable_no_claim",
  incomplete_recorded_history: "temporal_history_incomplete_no_subject",
  typed_retirement_evidence_unavailable: "typed_retirement_evidence_unavailable_no_claim",
  unknown_record_type: "unknown_record_type_no_claim",
  unknown_relationship_type: "unknown_relationship_type_no_claim",
  unsupported_semantics: "unsupported_semantics_no_claim",
} as const;

export class TemporalCompareValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "TemporalCompareValidationError";
    this.path = path;
  }
}

export class TemporalCompareChangedError extends Error {
  constructor() {
    super("Temporal comparison changed while the export was assembled.");
    this.name = "TemporalCompareChangedError";
  }
}

function fail(path: string, expectation: string): never {
  throw new TemporalCompareValidationError(path, expectation);
}

function exactRecord(value: unknown, path: string, keys: readonly string[], optional: readonly string[] = []): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) fail(path, "expected an object");
  const item = value as UnknownRecord;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) fail(`${path}.${key}`, "missing required field");
  }
  const allowed = new Set([...keys, ...optional]);
  for (const key of Object.keys(item)) {
    if (!allowed.has(key)) fail(`${path}.${key}`, "unknown field on a known contract object");
  }
  return item;
}

function boundedArray(value: unknown, path: string, max: number, min = 0): unknown[] {
  if (!Array.isArray(value) || value.length < min || value.length > max) {
    fail(path, `expected between ${min} and ${max} items`);
  }
  return value;
}

function text(value: unknown, path: string, max = Number.MAX_SAFE_INTEGER, allowEmpty = false): string {
  if (typeof value !== "string" || (!allowEmpty && value.length === 0) || [...value].length > max) {
    fail(path, `expected ${allowEmpty ? "a" : "a non-empty"} string of at most ${max} characters`);
  }
  return value;
}

function literal<T extends string>(value: unknown, path: string, choices: readonly T[]): T {
  if (typeof value !== "string" || !choices.includes(value as T)) fail(path, `expected one of: ${choices.join(", ")}`);
  return value as T;
}

function integer(value: unknown, path: string, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < min || value > max) {
    fail(path, `expected a safe integer between ${min} and ${max}`);
  }
  return value;
}

function nullableInteger(value: unknown, path: string): number | null {
  return value === null ? null : integer(value, path, 0, Number.MAX_SAFE_INTEGER);
}

function booleanValue(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(path, "expected a boolean");
  return value;
}

function requestUuid(value: unknown, path: string): string {
  const result = text(value, path, 36);
  if (!REQUEST_UUID_PATTERN.test(result)) fail(path, "expected a UUID");
  return result;
}

function uuid(value: unknown, path: string): string {
  const result = text(value, path, 36);
  if (!UUID_PATTERN.test(result)) fail(path, "expected a canonical lowercase UUID");
  return result;
}

function digest(value: unknown, path: string): string {
  const result = text(value, path, 71);
  if (!DIGEST_PATTERN.test(result)) fail(path, "expected a lowercase SHA-256 digest");
  return result;
}

function citationId(value: unknown, path: string): string {
  const result = text(value, path, 140);
  if (!CITATION_ID_PATTERN.test(result)) fail(path, "expected a canonical typed temporal citation ID");
  return result;
}

function snapshotToken(value: unknown, path: string): string {
  const result = text(value, path, 4096);
  if (!SNAPSHOT_TOKEN_PATTERN.test(result)) fail(path, "expected a canonical keyring-bound ts1 snapshot token");
  return result;
}

function validTimestampCalendar(value: string): boolean {
  const match = TIMESTAMP_PARTS.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[10]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[11]);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthLengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const normalizedMillis = Date.parse(value);
  const normalizedYear = Number.isFinite(normalizedMillis) ? new Date(normalizedMillis).getUTCFullYear() : 0;
  return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= monthLengths[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59 && offsetHour <= 23 && offsetMinute <= 59
    && normalizedYear >= 1 && normalizedYear <= 9999;
}

function requestTimestamp(value: unknown, path: string): string {
  const result = text(value, path);
  if (!AWARE_TIMESTAMP_PATTERN.test(result) || !validTimestampCalendar(result)) {
    fail(path, "expected a valid ISO-8601 timestamp with a timezone offset");
  }
  return result;
}

function fixedTimestamp(value: unknown, path: string): string {
  const result = text(value, path, 32);
  if (!FIXED_UTC_TIMESTAMP_PATTERN.test(result) || !validTimestampCalendar(result)) {
    fail(path, "expected a UTC timestamp with exactly six fractional digits");
  }
  return result;
}

function compareInstant(left: string, right: string): -1 | 0 | 1 {
  return compareInstantsAtMicrosecondPrecision(left, right);
}

function root(value: unknown, path: string, request = false): TemporalCompareRoot {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"]);
  (request ? requestUuid : uuid)(item.id, `${path}.id`);
  return value as TemporalCompareRoot;
}

function validateCanonicalRoots(roots: TemporalCompareRoot[], path: string): void {
  const ids = roots.map((entry) => entry.id.toLowerCase());
  const canonical = [...new Set(ids)].sort();
  if (ids.length !== canonical.length || ids.some((id, index) => id !== canonical[index])) {
    fail(path, "roots must be unique and sorted by canonical ID");
  }
}

export function validateTemporalCompareRequest(value: unknown, path = "request"): asserts value is TemporalCompareRequest {
  const item = exactRecord(value, path, ["project_id", "root_refs", "before_as_of", "after_as_of"], [
    "recorded_as_of", "snapshot_token", "direction", "max_depth", "max_records", "max_paths", "max_path_steps",
  ]);
  requestUuid(item.project_id, `${path}.project_id`);
  const roots = boundedArray(item.root_refs, `${path}.root_refs`, 100, 1).map((entry, index) => root(entry, `${path}.root_refs[${index}]`, true));
  validateCanonicalRoots(roots, `${path}.root_refs`);
  const before = requestTimestamp(item.before_as_of, `${path}.before_as_of`);
  const after = requestTimestamp(item.after_as_of, `${path}.after_as_of`);
  if (compareInstant(before, after) >= 0) fail(`${path}.before_as_of`, "must be earlier than after_as_of at microsecond precision");
  const recordedValue = item.recorded_as_of ?? null;
  const tokenValue = item.snapshot_token ?? null;
  if ((recordedValue === null) !== (tokenValue === null)) {
    fail(path, "recorded_as_of and snapshot_token must be supplied together");
  }
  if (recordedValue !== null) {
    const recorded = fixedTimestamp(recordedValue, `${path}.recorded_as_of`);
    snapshotToken(tokenValue, `${path}.snapshot_token`);
    if (compareInstant(after, recorded) > 0) fail(`${path}.after_as_of`, "must not exceed recorded_as_of");
  }
  if (item.direction !== undefined) literal(item.direction, `${path}.direction`, ["upstream", "downstream", "both"]);
  if (item.max_depth !== undefined) integer(item.max_depth, `${path}.max_depth`, 1, 32);
  if (item.max_records !== undefined) integer(item.max_records, `${path}.max_records`, 1, 500);
  if (item.max_paths !== undefined) integer(item.max_paths, `${path}.max_paths`, 1, 5000);
  if (item.max_path_steps !== undefined) integer(item.max_path_steps, `${path}.max_path_steps`, 1, 10_000);
  const maxRecords = item.max_records === undefined ? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_records : item.max_records as number;
  if (roots.length > maxRecords) fail(`${path}.max_records`, "must cover every requested root");
}

export function normalizeTemporalCompareRequest(request: TemporalCompareRequest): TemporalCompareRequest {
  validateTemporalCompareRequest(request);
  return {
    ...request,
    before_as_of: normalizeInstantToUtcMicroseconds(request.before_as_of),
    after_as_of: normalizeInstantToUtcMicroseconds(request.after_as_of),
    project_id: request.project_id.toLowerCase(),
    root_refs: request.root_refs.map((entry) => ({ entity_kind: "artifact_version", id: entry.id.toLowerCase() })),
  };
}

function validateTemporalCompareExportRequest(value: unknown): asserts value is TemporalCompareExportRequest {
  const item = exactRecord(value, "request", [
    "project_id", "root_refs", "before_as_of", "after_as_of", "recorded_as_of", "snapshot_token", "direction",
    "max_depth", "max_records", "max_paths", "max_path_steps", "expected_semantic_digest",
  ]);
  const displayRequest = { ...item };
  delete displayRequest.expected_semantic_digest;
  validateTemporalCompareRequest(displayRequest);
  fixedTimestamp(item.recorded_as_of, "request.recorded_as_of");
  snapshotToken(item.snapshot_token, "request.snapshot_token");
  digest(item.expected_semantic_digest, "request.expected_semantic_digest");
}

function validUnicodeScalarString(value: string, path: string): void {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) fail(path, "JCS cannot encode an unpaired Unicode surrogate");
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      fail(path, "JCS cannot encode an unpaired Unicode surrogate");
    }
  }
}

function canonicalStringByteLength(value: string, path: string): number {
  validUnicodeScalarString(value, path);
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function admitJsonValue(value: unknown, path: string, maximumBytes: number): number {
  const stack: Array<{ value: unknown; path: string; depth: number }> = [{ value, path, depth: 1 }];
  let nodes = 0;
  let bytes = 0;
  const addBytes = (count: number) => {
    bytes += count;
    if (bytes > maximumBytes) fail(path, `canonical JSON exceeds maximum byte length ${maximumBytes}`);
  };
  while (stack.length) {
    const frame = stack.pop()!;
    nodes += 1;
    if (nodes > TEMPORAL_COMPARE_MAX_ADMISSION_NODES) fail(path, `JSON exceeds maximum node count ${TEMPORAL_COMPARE_MAX_ADMISSION_NODES}`);
    if (frame.depth > TEMPORAL_COMPARE_MAX_ADMISSION_DEPTH) fail(frame.path, `JSON exceeds maximum depth ${TEMPORAL_COMPARE_MAX_ADMISSION_DEPTH}`);
    const entry = frame.value;
    if (entry === null) addBytes(4);
    else if (typeof entry === "boolean") addBytes(entry ? 4 : 5);
    else if (typeof entry === "number") {
      if (!Number.isFinite(entry) || !Number.isSafeInteger(entry)) fail(frame.path, "JCS requires a finite safe integer");
      addBytes(JSON.stringify(entry).length);
    } else if (typeof entry === "string") addBytes(canonicalStringByteLength(entry, frame.path));
    else if (Array.isArray(entry)) {
      addBytes(2 + Math.max(0, entry.length - 1));
      if (nodes + stack.length + entry.length > TEMPORAL_COMPARE_MAX_ADMISSION_NODES) {
        fail(path, `JSON exceeds maximum node count ${TEMPORAL_COMPARE_MAX_ADMISSION_NODES}`);
      }
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ value: entry[index], path: `${frame.path}[${index}]`, depth: frame.depth + 1 });
      }
    } else if (typeof entry === "object") {
      const keys = Object.keys(entry);
      nodes += keys.length;
      if (nodes > TEMPORAL_COMPARE_MAX_ADMISSION_NODES) fail(path, `JSON exceeds maximum node count ${TEMPORAL_COMPARE_MAX_ADMISSION_NODES}`);
      if (nodes + stack.length + keys.length > TEMPORAL_COMPARE_MAX_ADMISSION_NODES) {
        fail(path, `JSON exceeds maximum node count ${TEMPORAL_COMPARE_MAX_ADMISSION_NODES}`);
      }
      addBytes(2 + Math.max(0, keys.length - 1) + keys.length);
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        addBytes(canonicalStringByteLength(key, `${frame.path}.<key>`));
        stack.push({ value: (entry as UnknownRecord)[key], path: `${frame.path}.${key}`, depth: frame.depth + 1 });
      }
    } else fail(frame.path, "expected a JSON value");
  }
  return bytes;
}

type CanonicalFrame =
  | { kind: "value"; value: unknown; path: string }
  | { kind: "token"; value: string };

function canonicalJson(value: unknown, path = "$canonical"): string {
  const chunks: string[] = [];
  const stack: CanonicalFrame[] = [{ kind: "value", value, path }];
  while (stack.length) {
    const frame = stack.pop()!;
    if (frame.kind === "token") {
      chunks.push(frame.value);
      continue;
    }
    const entry = frame.value;
    if (entry === null || typeof entry === "boolean") chunks.push(String(entry));
    else if (typeof entry === "number") {
      if (!Number.isFinite(entry) || !Number.isSafeInteger(entry)) fail(frame.path, "JCS requires a finite safe integer");
      chunks.push(JSON.stringify(entry));
    } else if (typeof entry === "string") {
      validUnicodeScalarString(entry, frame.path);
      chunks.push(JSON.stringify(entry));
    } else if (Array.isArray(entry)) {
      chunks.push("[");
      stack.push({ kind: "token", value: "]" });
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ kind: "value", value: entry[index], path: `${frame.path}[${index}]` });
        if (index > 0) stack.push({ kind: "token", value: "," });
      }
    } else if (typeof entry === "object") {
      const item = entry as UnknownRecord;
      const keys = Object.keys(item).sort();
      chunks.push("{");
      stack.push({ kind: "token", value: "}" });
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        validUnicodeScalarString(key, `${frame.path}.<key>`);
        stack.push({ kind: "value", value: item[key], path: `${frame.path}.${key}` });
        stack.push({ kind: "token", value: ":" });
        stack.push({ kind: "token", value: JSON.stringify(key) });
        if (index > 0) stack.push({ kind: "token", value: "," });
      }
    } else fail(frame.path, "expected a JSON value");
  }
  return chunks.join("");
}

async function sha256(value: unknown): Promise<string> {
  if (!globalThis.crypto?.subtle) fail("$crypto", "WebCrypto SHA-256 is required to verify temporal comparison identities");
  const result = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalJson(value)));
  return `sha256:${[...new Uint8Array(result)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function limits(value: unknown, path: string): TemporalCompareLimits {
  const item = exactRecord(value, path, ["max_depth", "max_records", "max_paths", "max_path_steps", "max_causal_contexts"]);
  integer(item.max_depth, `${path}.max_depth`, 1, 32);
  integer(item.max_records, `${path}.max_records`, 1, 500);
  integer(item.max_paths, `${path}.max_paths`, 1, 5000);
  integer(item.max_path_steps, `${path}.max_path_steps`, 1, 10_000);
  if (integer(item.max_causal_contexts, `${path}.max_causal_contexts`, 10_000, 10_000) !== 10_000) fail(`${path}.max_causal_contexts`, "expected 10000");
  return value as TemporalCompareLimits;
}

function scope(value: unknown, path: string): TemporalCompareScope {
  const item = exactRecord(value, path, ["root_refs", "direction", "limits", "resource_limits_version"]);
  const roots = boundedArray(item.root_refs, `${path}.root_refs`, 100, 1).map((entry, index) => root(entry, `${path}.root_refs[${index}]`));
  validateCanonicalRoots(roots, `${path}.root_refs`);
  literal(item.direction, `${path}.direction`, ["upstream", "downstream", "both"]);
  limits(item.limits, `${path}.limits`);
  literal(item.resource_limits_version, `${path}.resource_limits_version`, [TEMPORAL_COMPARE_RESOURCE_LIMITS_VERSION]);
  return value as TemporalCompareScope;
}

function citationBase(item: UnknownRecord, path: string, recordKind: TemporalCompareCitation["record_kind"]): void {
  const id = citationId(item.citation_id, `${path}.citation_id`);
  const recordId = uuid(item.record_id, `${path}.record_id`);
  const ordinal = nullableInteger(item.record_ordinal, `${path}.record_ordinal`);
  fixedTimestamp(item.effective_at, `${path}.effective_at`);
  fixedTimestamp(item.recorded_at, `${path}.recorded_at`);
  const expectedOrdinal = recordKind === "decision_evidence";
  if (expectedOrdinal !== (ordinal !== null)) fail(`${path}.record_ordinal`, "only decision evidence citations carry an ordinal");
  const suffix = ordinal === null ? "" : `:${ordinal}`;
  if (id !== `tc:${recordKind}:${recordId}${suffix}`) fail(`${path}.citation_id`, "must derive from record kind, ID, and ordinal");
}

function citation(value: unknown, path: string): TemporalCompareCitation {
  const probe = exactRecord(value, path, ["record_kind"], [
    "citation_id", "record_id", "record_ordinal", "effective_at", "recorded_at", "artifact_version_id", "initial_lifecycle_state",
    "operation_id", "status", "binding_id", "direction", "role", "ordinal", "propagates_invalidity", "supersession_id",
    "old_artifact_version_id", "new_artifact_version_id", "change_type", "lifecycle_event_id", "event_type", "state",
    "decision_id", "decision_status_event_id", "retirement_event_id", "retired_entity_kind", "retired_entity_id",
    "retired_entity_ordinal", "retirement_type",
  ]);
  const kind = literal(probe.record_kind, `${path}.record_kind`, [
    "artifact_version", "operation_run", "binding", "supersession", "lifecycle_event", "decision", "decision_evidence",
    "decision_status_event", "topology_retirement",
  ] as const);
  const base = ["citation_id", "record_id", "record_ordinal", "effective_at", "recorded_at", "record_kind"] as const;
  if (kind === "artifact_version") {
    const item = exactRecord(value, path, [...base, "artifact_version_id", "initial_lifecycle_state"]);
    citationBase(item, path, kind);
    const subject = uuid(item.artifact_version_id, `${path}.artifact_version_id`);
    literal(item.initial_lifecycle_state, `${path}.initial_lifecycle_state`, ["active"]);
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal artifact_version_id");
  } else if (kind === "operation_run") {
    const item = exactRecord(value, path, [...base, "operation_id", "status"]);
    citationBase(item, path, kind);
    const subject = uuid(item.operation_id, `${path}.operation_id`);
    literal(item.status, `${path}.status`, ["completed"]);
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal operation_id");
  } else if (kind === "binding") {
    const item = exactRecord(value, path, [...base, "binding_id", "operation_id", "artifact_version_id", "direction", "role", "ordinal", "propagates_invalidity"]);
    citationBase(item, path, kind);
    const subject = uuid(item.binding_id, `${path}.binding_id`);
    uuid(item.operation_id, `${path}.operation_id`);
    uuid(item.artifact_version_id, `${path}.artifact_version_id`);
    const direction = literal(item.direction, `${path}.direction`, ["input", "output"]);
    text(item.role, `${path}.role`, 100);
    integer(item.ordinal, `${path}.ordinal`, 0, Number.MAX_SAFE_INTEGER);
    const propagates = booleanValue(item.propagates_invalidity, `${path}.propagates_invalidity`);
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal binding_id");
    if (direction === "output" && propagates) fail(`${path}.propagates_invalidity`, "output bindings cannot propagate invalidity");
  } else if (kind === "supersession") {
    const item = exactRecord(value, path, [...base, "supersession_id", "old_artifact_version_id", "new_artifact_version_id", "change_type"]);
    citationBase(item, path, kind);
    const subject = uuid(item.supersession_id, `${path}.supersession_id`);
    const oldId = uuid(item.old_artifact_version_id, `${path}.old_artifact_version_id`);
    const newId = uuid(item.new_artifact_version_id, `${path}.new_artifact_version_id`);
    text(item.change_type, `${path}.change_type`, 100);
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal supersession_id");
    if (oldId === newId) fail(path, "supersession endpoints must differ");
  } else if (kind === "lifecycle_event") {
    const item = exactRecord(value, path, [...base, "lifecycle_event_id", "artifact_version_id", "event_type", "state"]);
    citationBase(item, path, kind);
    const subject = uuid(item.lifecycle_event_id, `${path}.lifecycle_event_id`);
    uuid(item.artifact_version_id, `${path}.artifact_version_id`);
    const eventType = literal(item.event_type, `${path}.event_type`, ["invalidated", "superseded", "redacted", "abandoned", "declared_inapplicable", "reactivated"]);
    const state = literal(item.state, `${path}.state`, ["active", "invalidated", "superseded", "redacted", "abandoned", "inapplicable"]);
    const expectedState = eventType === "reactivated" ? "active" : eventType === "declared_inapplicable" ? "inapplicable" : eventType;
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal lifecycle_event_id");
    if (state !== expectedState) fail(`${path}.state`, "must agree with event_type");
  } else if (kind === "decision") {
    const item = exactRecord(value, path, [...base, "decision_id", "artifact_version_id"]);
    citationBase(item, path, kind);
    const subject = uuid(item.decision_id, `${path}.decision_id`);
    uuid(item.artifact_version_id, `${path}.artifact_version_id`);
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal decision_id");
  } else if (kind === "decision_evidence") {
    const item = exactRecord(value, path, [...base, "decision_id", "artifact_version_id", "ordinal"]);
    citationBase(item, path, kind);
    const decisionId = uuid(item.decision_id, `${path}.decision_id`);
    uuid(item.artifact_version_id, `${path}.artifact_version_id`);
    const ordinal = integer(item.ordinal, `${path}.ordinal`, 0, Number.MAX_SAFE_INTEGER);
    if (item.record_id !== decisionId || item.record_ordinal !== ordinal) fail(path, "evidence citation identity must equal decision and ordinal");
  } else if (kind === "decision_status_event") {
    const item = exactRecord(value, path, [...base, "decision_status_event_id", "decision_id", "artifact_version_id", "status"]);
    citationBase(item, path, kind);
    const subject = uuid(item.decision_status_event_id, `${path}.decision_status_event_id`);
    uuid(item.decision_id, `${path}.decision_id`);
    uuid(item.artifact_version_id, `${path}.artifact_version_id`);
    literal(item.status, `${path}.status`, DECISION_STATUSES);
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal decision_status_event_id");
  } else {
    const item = exactRecord(value, path, [...base, "retirement_event_id", "retired_entity_kind", "retired_entity_id", "retired_entity_ordinal", "retirement_type"]);
    citationBase(item, path, kind);
    const subject = uuid(item.retirement_event_id, `${path}.retirement_event_id`);
    const retiredKind = literal(item.retired_entity_kind, `${path}.retired_entity_kind`, ["artifact", "artifact_version", "operation_run", "binding", "supersession", "merge_input", "decision_evidence"]);
    uuid(item.retired_entity_id, `${path}.retired_entity_id`);
    const retiredOrdinal = nullableInteger(item.retired_entity_ordinal, `${path}.retired_entity_ordinal`);
    literal(item.retirement_type, `${path}.retirement_type`, RETIREMENT_ACTIONS);
    if (item.record_id !== subject) fail(`${path}.record_id`, "must equal retirement_event_id");
    if ((retiredKind === "decision_evidence") !== (retiredOrdinal !== null)) fail(`${path}.retired_entity_ordinal`, "only evidence retirement requires an entity ordinal");
  }
  return value as TemporalCompareCitation;
}

function topologyEdge(item: UnknownRecord, path: string): void {
  uuid(item.from_artifact_version_id, `${path}.from_artifact_version_id`);
  uuid(item.operation_id, `${path}.operation_id`);
  uuid(item.to_artifact_version_id, `${path}.to_artifact_version_id`);
  uuid(item.input_binding_id, `${path}.input_binding_id`);
  text(item.input_role, `${path}.input_role`, 100);
  uuid(item.output_binding_id, `${path}.output_binding_id`);
  text(item.output_role, `${path}.output_role`, 100);
  booleanValue(item.input_propagates_invalidity, `${path}.input_propagates_invalidity`);
}

function pathStep(value: unknown, path: string): TemporalComparePathStep {
  const probe = exactRecord(value, path, ["step_kind"], [
    "ordinal", "from_artifact_version_id", "operation_id", "to_artifact_version_id", "input_binding_id", "input_role",
    "output_binding_id", "output_role", "input_propagates_invalidity", "input_binding_citation_id", "operation_run_citation_id",
    "output_binding_citation_id", "old_artifact_version_id", "new_artifact_version_id", "supersession_id", "change_type",
    "supersession_citation_id",
  ]);
  const kind = literal(probe.step_kind, `${path}.step_kind`, ["operation", "supersession"]);
  if (kind === "operation") {
    const item = exactRecord(value, path, [
      "from_artifact_version_id", "operation_id", "to_artifact_version_id", "input_binding_id", "input_role", "output_binding_id",
      "output_role", "input_propagates_invalidity", "step_kind", "ordinal", "input_binding_citation_id", "operation_run_citation_id",
      "output_binding_citation_id",
    ]);
    topologyEdge(item, path);
    integer(item.ordinal, `${path}.ordinal`, 0, 9999);
    citationId(item.input_binding_citation_id, `${path}.input_binding_citation_id`);
    citationId(item.operation_run_citation_id, `${path}.operation_run_citation_id`);
    citationId(item.output_binding_citation_id, `${path}.output_binding_citation_id`);
  } else {
    const item = exactRecord(value, path, ["step_kind", "ordinal", "old_artifact_version_id", "new_artifact_version_id", "supersession_id", "change_type", "supersession_citation_id"]);
    integer(item.ordinal, `${path}.ordinal`, 0, 9999);
    const oldId = uuid(item.old_artifact_version_id, `${path}.old_artifact_version_id`);
    const newId = uuid(item.new_artifact_version_id, `${path}.new_artifact_version_id`);
    uuid(item.supersession_id, `${path}.supersession_id`);
    text(item.change_type, `${path}.change_type`, 100);
    citationId(item.supersession_citation_id, `${path}.supersession_citation_id`);
    if (oldId === newId) fail(path, "supersession path endpoints must differ");
  }
  return value as TemporalComparePathStep;
}

function stepEndpoints(step: TemporalComparePathStep): [string, string] {
  return step.step_kind === "operation"
    ? [step.from_artifact_version_id, step.to_artifact_version_id]
    : [step.old_artifact_version_id, step.new_artifact_version_id];
}

function comparePath(value: unknown, path: string): TemporalComparePath {
  const item = exactRecord(value, path, ["path_id", "root_ref", "temporal_side", "direction", "depth", "steps"]);
  digest(item.path_id, `${path}.path_id`);
  const rootRef = root(item.root_ref, `${path}.root_ref`);
  literal(item.temporal_side, `${path}.temporal_side`, ["before", "after"]);
  const direction = literal(item.direction, `${path}.direction`, ["self", "upstream", "downstream"]);
  const depth = integer(item.depth, `${path}.depth`, 0, 32);
  const steps = boundedArray(item.steps, `${path}.steps`, 32).map((entry, index) => pathStep(entry, `${path}.steps[${index}]`));
  if (depth !== steps.length) fail(`${path}.depth`, "must equal the path step count");
  steps.forEach((step, index) => {
    if (step.ordinal !== index) fail(`${path}.steps[${index}].ordinal`, "must be contiguous from zero");
  });
  if (depth === 0 && direction !== "self") fail(`${path}.direction`, "zero-hop paths must use self");
  if (depth > 0 && direction === "self") fail(`${path}.direction`, "non-empty paths cannot use self");
  if (depth > 0 && direction === "downstream") {
    if (stepEndpoints(steps[0]!)[0] !== rootRef.id) fail(path, "downstream path must start at the root input");
    for (let index = 1; index < steps.length; index += 1) {
      if (stepEndpoints(steps[index - 1]!)[1] !== stepEndpoints(steps[index]!)[0]) fail(path, "downstream steps must form a closed chain");
    }
  }
  if (depth > 0 && direction === "upstream") {
    if (stepEndpoints(steps[0]!)[1] !== rootRef.id) fail(path, "upstream path must start at the root output");
    for (let index = 1; index < steps.length; index += 1) {
      if (stepEndpoints(steps[index - 1]!)[0] !== stepEndpoints(steps[index]!)[1]) fail(path, "upstream steps must form a closed reverse chain");
    }
  }
  return value as TemporalComparePath;
}

function causalContext(value: unknown, path: string): TemporalCausalContext {
  const item = exactRecord(value, path, ["path_id", "root_ref", "temporal_side", "direction", "depth"]);
  digest(item.path_id, `${path}.path_id`);
  root(item.root_ref, `${path}.root_ref`);
  literal(item.temporal_side, `${path}.temporal_side`, ["before", "after"]);
  literal(item.direction, `${path}.direction`, ["self", "upstream", "downstream"]);
  integer(item.depth, `${path}.depth`, 0, 32);
  return value as TemporalCausalContext;
}

function compareTuple(left: readonly (string | number)[], right: readonly (string | number)[]): number {
  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    const a = left[index]!;
    const b = right[index]!;
    if (a === b) continue;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a) < String(b) ? -1 : 1;
  }
  return left.length - right.length;
}

function requireSortedUnique<T>(values: T[], key: (value: T) => readonly (string | number)[], path: string): void {
  const keys = values.map(key);
  for (let index = 0; index < keys.length; index += 1) {
    if (index > 0 && compareTuple(keys[index - 1]!, keys[index]!) >= 0) fail(path, "must be unique and deterministically sorted");
  }
}

function causalContexts(value: unknown, path: string): TemporalCausalContext[] {
  const result = boundedArray(value, path, 5000, 1).map((entry, index) => causalContext(entry, `${path}[${index}]`));
  requireSortedUnique(result, (entry) => [entry.path_id, entry.root_ref.entity_kind, entry.root_ref.id, entry.temporal_side, entry.direction, entry.depth], path);
  return result;
}

function topologyAddition(value: unknown, path: string): TemporalTopologyAddition {
  const item = exactRecord(value, path, [
    "from_artifact_version_id", "operation_id", "to_artifact_version_id", "input_binding_id", "input_role", "output_binding_id",
    "output_role", "input_propagates_invalidity", "causal_contexts", "input_binding_citation_id", "operation_run_citation_id",
    "output_binding_citation_id",
  ]);
  topologyEdge(item, path);
  causalContexts(item.causal_contexts, `${path}.causal_contexts`);
  citationId(item.input_binding_citation_id, `${path}.input_binding_citation_id`);
  citationId(item.operation_run_citation_id, `${path}.operation_run_citation_id`);
  citationId(item.output_binding_citation_id, `${path}.output_binding_citation_id`);
  return value as TemporalTopologyAddition;
}

function topologyRemoval(value: unknown, path: string): TemporalTopologyRemoval {
  const item = exactRecord(value, path, [
    "from_artifact_version_id", "operation_id", "to_artifact_version_id", "input_binding_id", "input_role", "output_binding_id",
    "output_role", "input_propagates_invalidity", "causal_contexts", "prior_input_binding_citation_id", "prior_operation_run_citation_id",
    "prior_output_binding_citation_id", "retirement_citation_id", "removal_category", "retirement_action",
  ]);
  topologyEdge(item, path);
  causalContexts(item.causal_contexts, `${path}.causal_contexts`);
  citationId(item.prior_input_binding_citation_id, `${path}.prior_input_binding_citation_id`);
  citationId(item.prior_operation_run_citation_id, `${path}.prior_operation_run_citation_id`);
  citationId(item.prior_output_binding_citation_id, `${path}.prior_output_binding_citation_id`);
  citationId(item.retirement_citation_id, `${path}.retirement_citation_id`);
  literal(item.removal_category, `${path}.removal_category`, ["binding", "operation_run"]);
  literal(item.retirement_action, `${path}.retirement_action`, RETIREMENT_ACTIONS);
  return value as TemporalTopologyRemoval;
}

function supersessionAddition(value: unknown, path: string): TemporalSupersessionAddition {
  const item = exactRecord(value, path, ["causal_contexts", "supersession_id", "old_artifact_version_id", "new_artifact_version_id", "change_type", "evidence_citation_id"]);
  causalContexts(item.causal_contexts, `${path}.causal_contexts`);
  uuid(item.supersession_id, `${path}.supersession_id`);
  const oldId = uuid(item.old_artifact_version_id, `${path}.old_artifact_version_id`);
  const newId = uuid(item.new_artifact_version_id, `${path}.new_artifact_version_id`);
  text(item.change_type, `${path}.change_type`, 100);
  citationId(item.evidence_citation_id, `${path}.evidence_citation_id`);
  if (oldId === newId) fail(path, "supersession endpoints must differ");
  return value as TemporalSupersessionAddition;
}

function validityChange(value: unknown, path: string): TemporalValidityChange {
  const item = exactRecord(value, path, [
    "causal_contexts", "artifact_version_id", "before_state", "after_state", "considered_valid_before", "considered_valid_after",
    "before_citation_id", "after_citation_id", "timeline_citation_ids", "timeline_complete",
  ]);
  causalContexts(item.causal_contexts, `${path}.causal_contexts`);
  uuid(item.artifact_version_id, `${path}.artifact_version_id`);
  const beforeState = text(item.before_state, `${path}.before_state`, 100);
  const afterState = text(item.after_state, `${path}.after_state`, 100);
  const beforeValid = booleanValue(item.considered_valid_before, `${path}.considered_valid_before`);
  const afterValid = booleanValue(item.considered_valid_after, `${path}.considered_valid_after`);
  const beforeCitation = citationId(item.before_citation_id, `${path}.before_citation_id`);
  const afterCitation = citationId(item.after_citation_id, `${path}.after_citation_id`);
  const timeline = boundedArray(item.timeline_citation_ids, `${path}.timeline_citation_ids`, 1000, 1).map((entry, index) => citationId(entry, `${path}.timeline_citation_ids[${index}]`));
  if (item.timeline_complete !== true) fail(`${path}.timeline_complete`, "expected true");
  if (beforeState === afterState && beforeValid === afterValid) fail(path, "validity delta must change state or validity");
  requireSortedUnique(timeline, (entry) => [entry], `${path}.timeline_citation_ids`);
  if (!timeline.includes(beforeCitation) || !timeline.includes(afterCitation)) fail(path, "validity observations must occur in the complete timeline");
  return value as TemporalValidityChange;
}

function decisionStatusChange(value: unknown, path: string): TemporalDecisionStatusChange {
  const item = exactRecord(value, path, [
    "causal_contexts", "decision_id", "before_status", "after_status", "before_citation_id", "after_citation_id",
    "timeline_citation_ids", "timeline_complete",
  ]);
  causalContexts(item.causal_contexts, `${path}.causal_contexts`);
  uuid(item.decision_id, `${path}.decision_id`);
  const before = literal(item.before_status, `${path}.before_status`, DECISION_STATUSES);
  const after = literal(item.after_status, `${path}.after_status`, DECISION_STATUSES);
  const beforeCitation = citationId(item.before_citation_id, `${path}.before_citation_id`);
  const afterCitation = citationId(item.after_citation_id, `${path}.after_citation_id`);
  const timeline = boundedArray(item.timeline_citation_ids, `${path}.timeline_citation_ids`, 100, 2).map((entry, index) => citationId(entry, `${path}.timeline_citation_ids[${index}]`));
  if (item.timeline_complete !== true) fail(`${path}.timeline_complete`, "expected true");
  if (before === after) fail(path, "decision status delta must change status");
  requireSortedUnique(timeline, (entry) => [entry], `${path}.timeline_citation_ids`);
  if (!timeline.includes(beforeCitation) || !timeline.includes(afterCitation)) fail(path, "status observations must occur in the complete timeline");
  return value as TemporalDecisionStatusChange;
}

function evidenceAddition(value: unknown, path: string): TemporalEvidenceMembershipAddition {
  const item = exactRecord(value, path, ["causal_contexts", "decision_id", "artifact_version_id", "ordinal", "evidence_citation_id"]);
  causalContexts(item.causal_contexts, `${path}.causal_contexts`);
  uuid(item.decision_id, `${path}.decision_id`);
  uuid(item.artifact_version_id, `${path}.artifact_version_id`);
  integer(item.ordinal, `${path}.ordinal`, 0, Number.MAX_SAFE_INTEGER);
  citationId(item.evidence_citation_id, `${path}.evidence_citation_id`);
  return value as TemporalEvidenceMembershipAddition;
}

function evidenceRemoval(value: unknown, path: string): TemporalEvidenceMembershipRemoval {
  const item = exactRecord(value, path, [
    "causal_contexts", "decision_id", "artifact_version_id", "ordinal", "prior_evidence_citation_id", "retirement_citation_id",
    "removal_category", "retirement_action",
  ]);
  causalContexts(item.causal_contexts, `${path}.causal_contexts`);
  uuid(item.decision_id, `${path}.decision_id`);
  uuid(item.artifact_version_id, `${path}.artifact_version_id`);
  integer(item.ordinal, `${path}.ordinal`, 0, Number.MAX_SAFE_INTEGER);
  citationId(item.prior_evidence_citation_id, `${path}.prior_evidence_citation_id`);
  citationId(item.retirement_citation_id, `${path}.retirement_citation_id`);
  literal(item.removal_category, `${path}.removal_category`, ["decision_evidence"]);
  literal(item.retirement_action, `${path}.retirement_action`, RETIREMENT_ACTIONS);
  return value as TemporalEvidenceMembershipRemoval;
}

function entityRef(value: unknown, path: string): TemporalEntityRef {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, [
    "artifact_version", "operation_run", "binding", "supersession", "lifecycle_event", "decision", "decision_evidence",
    "decision_status_event", "binding_state_event", "operation_state_event", "topology_retirement",
  ]);
  uuid(item.id, `${path}.id`);
  return value as TemporalEntityRef;
}

function gap(value: unknown, path: string): TemporalCompareGap {
  const item = exactRecord(value, path, ["code", "marker", "subject"]);
  const code = literal(item.code, `${path}.code`, GAP_CODES);
  const marker = literal(item.marker, `${path}.marker`, Object.values(GAP_MARKERS));
  if (marker !== GAP_MARKERS[code]) fail(`${path}.marker`, "must be the fixed disclosure-safe marker for code");
  if (item.subject !== null) entityRef(item.subject, `${path}.subject`);
  if (code === "incomplete_recorded_history" && item.subject !== null) fail(`${path}.subject`, "history gaps must not identify a hidden subject");
  return value as TemporalCompareGap;
}

function completenessState(value: unknown, path: string, withEffective: boolean): TemporalCompletenessState | TemporalCompareSideCompleteness {
  const item = exactRecord(value, path, withEffective ? ["status", "gaps", "effective_as_of"] : ["status", "gaps"]);
  const status = literal(item.status, `${path}.status`, ["complete", "incomplete"]);
  const gaps = boundedArray(item.gaps, `${path}.gaps`, 1000).map((entry, index) => gap(entry, `${path}.gaps[${index}]`));
  if ((status === "incomplete") !== (gaps.length > 0)) fail(`${path}.status`, "must agree with the gap set");
  requireSortedUnique(gaps, (entry) => [entry.code, entry.marker, entry.subject?.entity_kind ?? "", entry.subject?.id ?? ""], `${path}.gaps`);
  if (withEffective) fixedTimestamp(item.effective_as_of, `${path}.effective_as_of`);
  return value as TemporalCompletenessState | TemporalCompareSideCompleteness;
}

function edgeKey(value: TemporalTopologyEdge): readonly string[] {
  return [
    value.from_artifact_version_id, value.operation_id, value.to_artifact_version_id, value.input_binding_id,
    value.input_role, value.output_binding_id, value.output_role, String(value.input_propagates_invalidity),
  ];
}

function pathContainsArtifact(path: TemporalComparePath, artifactId: string): boolean {
  return path.root_ref.id === artifactId || path.steps.some((step) => stepEndpoints(step).includes(artifactId));
}

function setEquals<T>(left: Set<T>, right: Set<T>): boolean {
  return left.size === right.size && [...left].every((entry) => right.has(entry));
}

function compareCitationsByObservation(left: TemporalCompareCitation, right: TemporalCompareCitation, lifecycleTieBreak = false): number {
  const effective = compareInstant(left.effective_at, right.effective_at);
  if (effective !== 0) return effective;
  const recorded = compareInstant(left.recorded_at, right.recorded_at);
  if (recorded !== 0) return recorded;
  if (lifecycleTieBreak) {
    const leftLifecycle = left.record_kind === "lifecycle_event" ? 1 : 0;
    const rightLifecycle = right.record_kind === "lifecycle_event" ? 1 : 0;
    if (leftLifecycle !== rightLifecycle) return leftLifecycle - rightLifecycle;
  }
  return left.record_id < right.record_id ? -1 : left.record_id > right.record_id ? 1 : 0;
}

const VISIBLE_UUID_FIELDS = new Set([
  "record_id",
  "artifact_version_id",
  "from_artifact_version_id",
  "to_artifact_version_id",
  "old_artifact_version_id",
  "new_artifact_version_id",
  "operation_id",
  "binding_id",
  "input_binding_id",
  "output_binding_id",
  "supersession_id",
  "lifecycle_event_id",
  "decision_id",
  "decision_status_event_id",
  "retirement_event_id",
  "retired_entity_id",
]);

function collectVisibleUuids(value: unknown): Set<string> {
  const result = new Set<string>();
  const stack: Array<{ value: unknown; field: string | null; parent: UnknownRecord | null }> = [{ value, field: null, parent: null }];
  while (stack.length) {
    const frame = stack.pop()!;
    const entry = frame.value;
    if (typeof entry === "string") {
      const typedId = frame.field !== null && (VISIBLE_UUID_FIELDS.has(frame.field)
        || (frame.field === "id" && typeof frame.parent?.entity_kind === "string"));
      if (typedId && UUID_PATTERN.test(entry)) result.add(entry);
    } else if (Array.isArray(entry)) {
      for (const child of entry) stack.push({ value: child, field: null, parent: null });
    } else if (entry !== null && typeof entry === "object") {
      const item = entry as UnknownRecord;
      for (const [field, child] of Object.entries(item)) stack.push({ value: child, field, parent: item });
    }
  }
  return result;
}

const TYPED_UUID_FIELDS: Record<string, string> = {
  artifact_version_id: "artifact_version",
  from_artifact_version_id: "artifact_version",
  to_artifact_version_id: "artifact_version",
  old_artifact_version_id: "artifact_version",
  new_artifact_version_id: "artifact_version",
  operation_id: "operation_run",
  binding_id: "binding",
  input_binding_id: "binding",
  output_binding_id: "binding",
  supersession_id: "supersession",
  lifecycle_event_id: "lifecycle_event",
  decision_id: "decision",
  decision_status_event_id: "decision_status_event",
  retirement_event_id: "topology_retirement",
};

function typedIdentityCollisionCount(value: unknown): number {
  const typed = new Set<string>();
  const stack = [value];
  while (stack.length) {
    const entry = stack.pop();
    if (Array.isArray(entry)) {
      stack.push(...entry);
      continue;
    }
    if (entry === null || typeof entry !== "object") continue;
    const item = entry as UnknownRecord;
    if (typeof item.entity_kind === "string" && typeof item.id === "string" && UUID_PATTERN.test(item.id)) typed.add(`${item.entity_kind}\0${item.id}`);
    if (typeof item.record_kind === "string" && item.record_kind !== "decision_evidence" && typeof item.record_id === "string" && UUID_PATTERN.test(item.record_id)) {
      typed.add(`${item.record_kind}\0${item.record_id}`);
    }
    if (item.record_kind === "topology_retirement" && item.retired_entity_kind !== "decision_evidence" && typeof item.retired_entity_kind === "string" && typeof item.retired_entity_id === "string") {
      typed.add(`${item.retired_entity_kind}\0${item.retired_entity_id}`);
    }
    for (const [key, child] of Object.entries(item)) {
      const kind = TYPED_UUID_FIELDS[key];
      if (kind && typeof child === "string" && UUID_PATTERN.test(child)) typed.add(`${kind}\0${child}`);
      stack.push(child);
    }
  }
  const rawIds = new Set([...typed].map((entry) => entry.slice(entry.indexOf("\0") + 1)));
  return typed.size - rawIds.size;
}

function validateVisibleRecordBudget(document: TemporalCompareDocument, deltas: CausallyLinkedDelta[]): void {
  const visibleParts = [document.scope.root_refs, document.paths, deltas, document.citations];
  const recordRefs = collectVisibleUuids(visibleParts);
  for (const state of [document.completeness.before, document.completeness.after, document.completeness.comparison]) {
    for (const entry of state.gaps) {
      if (entry.subject !== null && !recordRefs.has(entry.subject.id)) fail("$.data.document.completeness", "gap subjects must be admitted by visible evidence");
    }
  }
  for (const id of collectVisibleUuids(document.completeness)) recordRefs.add(id);
  const composite = new Set<string>();
  for (const entry of document.citations) {
    if (entry.record_kind === "decision_evidence") composite.add(`decision_evidence\0${entry.decision_id}\0${entry.ordinal}`);
    if (entry.record_kind === "topology_retirement" && entry.retired_entity_kind === "decision_evidence" && entry.retired_entity_ordinal !== null) {
      composite.add(`decision_evidence\0${entry.retired_entity_id}\0${entry.retired_entity_ordinal}`);
    }
  }
  for (const entry of [...document.evidence_membership.added, ...document.evidence_membership.removed]) {
    composite.add(`decision_evidence\0${entry.decision_id}\0${entry.ordinal}`);
  }
  const collisionCount = typedIdentityCollisionCount([...visibleParts, document.completeness]);
  if (recordRefs.size + composite.size + collisionCount > document.scope.limits.max_records) {
    fail("$.data.document.scope.limits.max_records", "visible record identities exceed max_records");
  }
}

type CausallyLinkedDelta =
  | TemporalTopologyAddition
  | TemporalTopologyRemoval
  | TemporalSupersessionAddition
  | TemporalValidityChange
  | TemporalDecisionStatusChange
  | TemporalEvidenceMembershipAddition
  | TemporalEvidenceMembershipRemoval;

function validateDocument(value: unknown, path: string): TemporalCompareDocument {
  admitJsonValue(value, path, TEMPORAL_COMPARE_MAX_DOCUMENT_CANONICAL_BYTES);
  const item = exactRecord(value, path, [
    "contract_version", "project_id", "before_as_of", "after_as_of", "recorded_as_of", "snapshot_token", "source_contract_version",
    "temporal_basis", "scope", "summary", "paths", "topology", "supersessions_added", "validity_changes",
    "decision_status_changes", "evidence_membership", "citations", "completeness",
  ]);
  literal(item.contract_version, `${path}.contract_version`, [TEMPORAL_COMPARE_CONTRACT_VERSION]);
  uuid(item.project_id, `${path}.project_id`);
  const beforeAsOf = fixedTimestamp(item.before_as_of, `${path}.before_as_of`);
  const afterAsOf = fixedTimestamp(item.after_as_of, `${path}.after_as_of`);
  const recordedAsOf = fixedTimestamp(item.recorded_as_of, `${path}.recorded_as_of`);
  if (compareInstant(beforeAsOf, afterAsOf) >= 0) fail(`${path}.before_as_of`, "must precede after_as_of");
  if (compareInstant(afterAsOf, recordedAsOf) > 0) fail(`${path}.after_as_of`, "must not exceed recorded_as_of");
  snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  literal(item.source_contract_version, `${path}.source_contract_version`, ["thread.lineage.v2"]);
  literal(item.temporal_basis, `${path}.temporal_basis`, ["effective_and_recorded_time"]);
  const exactScope = scope(item.scope, `${path}.scope`);

  const summaryItem = exactRecord(item.summary, `${path}.summary`, [
    "topology_added", "topology_removed", "supersessions_added", "validity_changed", "decision_status_changed", "evidence_added", "evidence_removed",
  ]);
  const summary: TemporalCompareSummary = {
    topology_added: integer(summaryItem.topology_added, `${path}.summary.topology_added`, 0, 5000),
    topology_removed: integer(summaryItem.topology_removed, `${path}.summary.topology_removed`, 0, 5000),
    supersessions_added: integer(summaryItem.supersessions_added, `${path}.summary.supersessions_added`, 0, 500),
    validity_changed: integer(summaryItem.validity_changed, `${path}.summary.validity_changed`, 0, 500),
    decision_status_changed: integer(summaryItem.decision_status_changed, `${path}.summary.decision_status_changed`, 0, 500),
    evidence_added: integer(summaryItem.evidence_added, `${path}.summary.evidence_added`, 0, 500),
    evidence_removed: integer(summaryItem.evidence_removed, `${path}.summary.evidence_removed`, 0, 500),
  };
  const paths = boundedArray(item.paths, `${path}.paths`, 5000).map((entry, index) => comparePath(entry, `${path}.paths[${index}]`));
  const topologyItem = exactRecord(item.topology, `${path}.topology`, ["added", "removed"]);
  const topologyAdded = boundedArray(topologyItem.added, `${path}.topology.added`, 5000).map((entry, index) => topologyAddition(entry, `${path}.topology.added[${index}]`));
  const topologyRemoved = boundedArray(topologyItem.removed, `${path}.topology.removed`, 5000).map((entry, index) => topologyRemoval(entry, `${path}.topology.removed[${index}]`));
  const supersessions = boundedArray(item.supersessions_added, `${path}.supersessions_added`, 500).map((entry, index) => supersessionAddition(entry, `${path}.supersessions_added[${index}]`));
  const validity = boundedArray(item.validity_changes, `${path}.validity_changes`, 500).map((entry, index) => validityChange(entry, `${path}.validity_changes[${index}]`));
  const decisionStatuses = boundedArray(item.decision_status_changes, `${path}.decision_status_changes`, 500).map((entry, index) => decisionStatusChange(entry, `${path}.decision_status_changes[${index}]`));
  const evidenceItem = exactRecord(item.evidence_membership, `${path}.evidence_membership`, ["added", "removed"]);
  const evidenceAdded = boundedArray(evidenceItem.added, `${path}.evidence_membership.added`, 500).map((entry, index) => evidenceAddition(entry, `${path}.evidence_membership.added[${index}]`));
  const evidenceRemoved = boundedArray(evidenceItem.removed, `${path}.evidence_membership.removed`, 500).map((entry, index) => evidenceRemoval(entry, `${path}.evidence_membership.removed[${index}]`));
  const citations = boundedArray(item.citations, `${path}.citations`, 15_000).map((entry, index) => citation(entry, `${path}.citations[${index}]`));
  const completenessItem = exactRecord(item.completeness, `${path}.completeness`, ["before", "after", "comparison"]);
  const beforeCompleteness = completenessState(completenessItem.before, `${path}.completeness.before`, true) as TemporalCompareSideCompleteness;
  const afterCompleteness = completenessState(completenessItem.after, `${path}.completeness.after`, true) as TemporalCompareSideCompleteness;
  const comparisonCompleteness = completenessState(completenessItem.comparison, `${path}.completeness.comparison`, false) as TemporalCompletenessState;

  if (beforeCompleteness.effective_as_of !== beforeAsOf) fail(`${path}.completeness.before.effective_as_of`, "must equal before_as_of");
  if (afterCompleteness.effective_as_of !== afterAsOf) fail(`${path}.completeness.after.effective_as_of`, "must equal after_as_of");
  if ((beforeCompleteness.status === "incomplete" || afterCompleteness.status === "incomplete") && comparisonCompleteness.status !== "incomplete") {
    fail(`${path}.completeness.comparison.status`, "an incomplete side requires an incomplete comparison");
  }
  const beforeHistory = beforeCompleteness.gaps.filter((entry) => entry.code === "incomplete_recorded_history");
  const afterHistory = afterCompleteness.gaps.filter((entry) => entry.code === "incomplete_recorded_history");
  if (canonicalJson(beforeHistory) !== canonicalJson(afterHistory)) fail(`${path}.completeness`, "history incompleteness must use the identical marker on both sides");
  if (beforeHistory.length > 1) fail(`${path}.completeness`, "history incompleteness uses exactly one fixed marker");
  const comparisonHistory = comparisonCompleteness.gaps.filter((entry) => entry.code === "incomplete_recorded_history");
  if (beforeHistory.length === 1 && (comparisonHistory.length !== 1 || comparisonHistory[0]!.subject !== null)) {
    fail(`${path}.completeness.comparison`, "side history gaps require one fixed subjectless comparison marker");
  }

  const expectedSummary: TemporalCompareSummary = {
    topology_added: topologyAdded.length,
    topology_removed: topologyRemoved.length,
    supersessions_added: supersessions.length,
    validity_changed: validity.length,
    decision_status_changed: decisionStatuses.length,
    evidence_added: evidenceAdded.length,
    evidence_removed: evidenceRemoved.length,
  };
  for (const key of Object.keys(expectedSummary) as Array<keyof TemporalCompareSummary>) {
    if (summary[key] !== expectedSummary[key]) fail(`${path}.summary.${key}`, `expected emitted delta count ${expectedSummary[key]}`);
  }
  const historyIncomplete = comparisonHistory.length > 0;
  if (historyIncomplete && (paths.length || topologyAdded.length || topologyRemoved.length || supersessions.length || validity.length
    || decisionStatuses.length || evidenceAdded.length || evidenceRemoved.length || citations.length)) {
    fail(path, "incomplete recorded history requires atomic delta omission");
  }

  requireSortedUnique(paths, (entry) => [entry.path_id], `${path}.paths`);
  const rootKeys = new Set(exactScope.root_refs.map((entry) => `${entry.entity_kind}\0${entry.id}`));
  const allowedDirections = exactScope.direction === "both" ? new Set(["upstream", "downstream"]) : new Set([exactScope.direction]);
  for (const entry of paths) {
    if (!rootKeys.has(`${entry.root_ref.entity_kind}\0${entry.root_ref.id}`)) fail(`${path}.paths`, "path roots must belong to scope.root_refs");
    if (entry.direction !== "self" && !allowedDirections.has(entry.direction)) fail(`${path}.paths`, "path direction exceeds scope.direction");
  }
  requireSortedUnique(topologyAdded, edgeKey, `${path}.topology.added`);
  requireSortedUnique(topologyRemoved, edgeKey, `${path}.topology.removed`);
  requireSortedUnique(supersessions, (entry) => [entry.supersession_id], `${path}.supersessions_added`);
  requireSortedUnique(validity, (entry) => [entry.artifact_version_id], `${path}.validity_changes`);
  requireSortedUnique(decisionStatuses, (entry) => [entry.decision_id], `${path}.decision_status_changes`);
  const evidenceKey = (entry: TemporalEvidenceMembershipAddition | TemporalEvidenceMembershipRemoval): readonly (string | number)[] => [entry.decision_id, entry.ordinal, entry.artifact_version_id];
  requireSortedUnique(evidenceAdded, evidenceKey, `${path}.evidence_membership.added`);
  requireSortedUnique(evidenceRemoved, evidenceKey, `${path}.evidence_membership.removed`);

  if (paths.length > exactScope.limits.max_paths) fail(`${path}.paths`, "exceeds scope max_paths");
  if (paths.some((entry) => entry.depth > exactScope.limits.max_depth)) fail(`${path}.paths`, "a path exceeds scope max_depth");
  const emittedStepLimit = Math.min(exactScope.limits.max_path_steps, exactScope.limits.max_paths * exactScope.limits.max_depth, 10_000);
  if (paths.reduce((count, entry) => count + entry.steps.length, 0) > emittedStepLimit) fail(`${path}.paths`, "path steps exceed the dynamic scope bound");

  const deltas: CausallyLinkedDelta[] = [...topologyAdded, ...topologyRemoved, ...supersessions, ...validity, ...decisionStatuses, ...evidenceAdded, ...evidenceRemoved];
  if (deltas.reduce((count, entry) => count + entry.causal_contexts.length, 0) > exactScope.limits.max_causal_contexts) {
    fail(path, "causal contexts exceed the aggregate cap");
  }
  const pathsById = new Map(paths.map((entry) => [entry.path_id, entry]));
  const referencedPaths = new Set<string>();
  for (const delta of deltas) {
    for (const context of delta.causal_contexts) {
      const exactPath = pathsById.get(context.path_id);
      if (!exactPath || exactPath.root_ref.entity_kind !== context.root_ref.entity_kind || exactPath.root_ref.id !== context.root_ref.id
        || exactPath.temporal_side !== context.temporal_side || exactPath.direction !== context.direction || exactPath.depth !== context.depth) {
        fail(path, "delta causal contexts must exactly reference emitted paths");
      }
      referencedPaths.add(context.path_id);
    }
  }
  if (!setEquals(referencedPaths, new Set(pathsById.keys()))) fail(`${path}.paths`, "must exactly equal delta-referenced contexts");

  requireSortedUnique(citations, (entry) => [entry.citation_id], `${path}.citations`);
  const citationsById = new Map(citations.map((entry) => [entry.citation_id, entry]));
  const referencedCitations = new Set<string>();
  const cite = (id: string, kinds: readonly TemporalCompareCitation["record_kind"][], evidencePath: string): TemporalCompareCitation => {
    const result = citationsById.get(id);
    if (!result || !kinds.includes(result.record_kind)) fail(evidencePath, "citation kind does not match asserted evidence");
    referencedCitations.add(id);
    return result;
  };
  const contextsContain = (delta: CausallyLinkedDelta, artifactId: string, evidencePath: string) => {
    if (!delta.causal_contexts.some((context) => pathContainsArtifact(pathsById.get(context.path_id)!, artifactId))) {
      fail(evidencePath, "delta subject must occur on a referenced causal path");
    }
  };
  const requireContextSide = (delta: CausallyLinkedDelta, side: TemporalCompareSide, evidencePath: string) => {
    if (delta.causal_contexts.some((context) => context.temporal_side !== side)) fail(evidencePath, `causal paths must use the ${side} side`);
  };

  for (const [pathIndex, exactPath] of paths.entries()) {
    const sideCut = exactPath.temporal_side === "before" ? beforeAsOf : afterAsOf;
    for (const [stepIndex, step] of exactPath.steps.entries()) {
      const evidencePath = `${path}.paths[${pathIndex}].steps[${stepIndex}]`;
      if (step.step_kind === "operation") {
        const input = cite(step.input_binding_citation_id, ["binding"], evidencePath) as TemporalBindingCitation;
        const operation = cite(step.operation_run_citation_id, ["operation_run"], evidencePath) as TemporalOperationRunCitation;
        const output = cite(step.output_binding_citation_id, ["binding"], evidencePath) as TemporalBindingCitation;
        if (!(input.binding_id === step.input_binding_id && input.operation_id === step.operation_id
          && input.artifact_version_id === step.from_artifact_version_id && input.direction === "input" && input.role === step.input_role
          && input.propagates_invalidity === step.input_propagates_invalidity && operation.operation_id === step.operation_id
          && output.binding_id === step.output_binding_id && output.operation_id === step.operation_id
          && output.artifact_version_id === step.to_artifact_version_id && output.direction === "output" && output.role === step.output_role
          && [input, operation, output].every((entry) => compareInstant(entry.effective_at, sideCut) <= 0))) {
          fail(evidencePath, "operation citations do not prove the exact visible edge");
        }
      } else {
        const evidence = cite(step.supersession_citation_id, ["supersession"], evidencePath) as TemporalSupersessionCitation;
        if (!(evidence.supersession_id === step.supersession_id && evidence.old_artifact_version_id === step.old_artifact_version_id
          && evidence.new_artifact_version_id === step.new_artifact_version_id && evidence.change_type === step.change_type
          && compareInstant(evidence.effective_at, sideCut) <= 0)) fail(evidencePath, "supersession citation does not prove the exact edge");
      }
    }
  }

  for (const [index, delta] of topologyAdded.entries()) {
    const evidencePath = `${path}.topology.added[${index}]`;
    requireContextSide(delta, "after", evidencePath);
    const input = cite(delta.input_binding_citation_id, ["binding"], evidencePath) as TemporalBindingCitation;
    const operation = cite(delta.operation_run_citation_id, ["operation_run"], evidencePath) as TemporalOperationRunCitation;
    const output = cite(delta.output_binding_citation_id, ["binding"], evidencePath) as TemporalBindingCitation;
    if (!(input.binding_id === delta.input_binding_id && input.operation_id === delta.operation_id && input.artifact_version_id === delta.from_artifact_version_id
      && input.direction === "input" && input.role === delta.input_role && input.propagates_invalidity === delta.input_propagates_invalidity
      && operation.operation_id === delta.operation_id && output.binding_id === delta.output_binding_id && output.operation_id === delta.operation_id
      && output.artifact_version_id === delta.to_artifact_version_id && output.direction === "output" && output.role === delta.output_role)) {
      fail(evidencePath, "addition evidence does not prove the exact edge");
    }
    const creation = [input, operation, output];
    if (creation.some((entry) => compareInstant(entry.effective_at, afterAsOf) > 0)
      || !creation.some((entry) => compareInstant(entry.effective_at, beforeAsOf) > 0)) fail(evidencePath, "addition must become effective in the comparison window");
    if (!delta.causal_contexts.some((context) => pathsById.get(context.path_id)!.steps.some((step) => step.step_kind === "operation" && compareTuple(edgeKey(step), edgeKey(delta)) === 0))) {
      fail(evidencePath, "addition must occur on a referenced path");
    }
  }

  for (const [index, delta] of topologyRemoved.entries()) {
    const evidencePath = `${path}.topology.removed[${index}]`;
    requireContextSide(delta, "before", evidencePath);
    const input = cite(delta.prior_input_binding_citation_id, ["binding"], evidencePath) as TemporalBindingCitation;
    const operation = cite(delta.prior_operation_run_citation_id, ["operation_run"], evidencePath) as TemporalOperationRunCitation;
    const output = cite(delta.prior_output_binding_citation_id, ["binding"], evidencePath) as TemporalBindingCitation;
    const retirement = cite(delta.retirement_citation_id, ["topology_retirement"], evidencePath) as TemporalTopologyRetirementCitation;
    if (!(input.binding_id === delta.input_binding_id && input.operation_id === delta.operation_id && input.artifact_version_id === delta.from_artifact_version_id
      && input.direction === "input" && input.role === delta.input_role && input.propagates_invalidity === delta.input_propagates_invalidity
      && operation.operation_id === delta.operation_id && output.binding_id === delta.output_binding_id && output.operation_id === delta.operation_id
      && output.artifact_version_id === delta.to_artifact_version_id && output.direction === "output" && output.role === delta.output_role)) {
      fail(evidencePath, "prior evidence does not prove the removed edge");
    }
    if ([input, operation, output].some((entry) => compareInstant(entry.effective_at, beforeAsOf) > 0)) fail(evidencePath, "removed edge must be complete at before_as_of");
    const targetMatches = (delta.removal_category === "operation_run" && retirement.retired_entity_kind === "operation_run" && retirement.retired_entity_id === delta.operation_id)
      || (delta.removal_category === "binding" && retirement.retired_entity_kind === "binding" && [delta.input_binding_id, delta.output_binding_id].includes(retirement.retired_entity_id));
    if (!targetMatches || retirement.retirement_type !== delta.retirement_action || compareInstant(beforeAsOf, retirement.effective_at) >= 0
      || compareInstant(retirement.effective_at, afterAsOf) > 0) fail(evidencePath, "removal requires exact in-window retirement evidence");
    if (!delta.causal_contexts.some((context) => pathsById.get(context.path_id)!.steps.some((step) => step.step_kind === "operation" && compareTuple(edgeKey(step), edgeKey(delta)) === 0))) {
      fail(evidencePath, "removal must occur on a referenced prior path");
    }
  }

  for (const [index, delta] of supersessions.entries()) {
    const evidencePath = `${path}.supersessions_added[${index}]`;
    requireContextSide(delta, "after", evidencePath);
    const evidence = cite(delta.evidence_citation_id, ["supersession"], evidencePath) as TemporalSupersessionCitation;
    if (!(evidence.supersession_id === delta.supersession_id && evidence.old_artifact_version_id === delta.old_artifact_version_id
      && evidence.new_artifact_version_id === delta.new_artifact_version_id && evidence.change_type === delta.change_type
      && compareInstant(beforeAsOf, evidence.effective_at) < 0 && compareInstant(evidence.effective_at, afterAsOf) <= 0)) {
      fail(evidencePath, "supersession citation does not prove the delta");
    }
    contextsContain(delta, delta.old_artifact_version_id, evidencePath);
    contextsContain(delta, delta.new_artifact_version_id, evidencePath);
    if (!delta.causal_contexts.some((context) => pathsById.get(context.path_id)!.steps.some((step) => step.step_kind === "supersession"
      && step.supersession_id === delta.supersession_id && step.old_artifact_version_id === delta.old_artifact_version_id
      && step.new_artifact_version_id === delta.new_artifact_version_id && step.change_type === delta.change_type))) {
      fail(evidencePath, "supersession delta must occur on a cited path");
    }
  }

  const validityObservation = (entry: TemporalArtifactVersionCitation | TemporalLifecycleCitation): [string, string, boolean] => {
    if (entry.record_kind === "artifact_version") return [entry.artifact_version_id, "active", true];
    return [entry.artifact_version_id, entry.state, !["invalidated", "superseded", "redacted", "abandoned", "inapplicable"].includes(entry.state)];
  };
  for (const [index, delta] of validity.entries()) {
    const evidencePath = `${path}.validity_changes[${index}]`;
    requireContextSide(delta, "after", evidencePath);
    const kinds = ["artifact_version", "lifecycle_event"] as const;
    const before = cite(delta.before_citation_id, kinds, evidencePath) as TemporalArtifactVersionCitation | TemporalLifecycleCitation;
    const after = cite(delta.after_citation_id, kinds, evidencePath) as TemporalArtifactVersionCitation | TemporalLifecycleCitation;
    const timeline = delta.timeline_citation_ids.map((id) => cite(id, kinds, evidencePath) as TemporalArtifactVersionCitation | TemporalLifecycleCitation);
    if (timeline.some((entry) => validityObservation(entry)[0] !== delta.artifact_version_id)) fail(evidencePath, "validity timeline contains another subject");
    const exactTimeline = new Set(citations.filter((entry): entry is TemporalArtifactVersionCitation | TemporalLifecycleCitation => entry.record_kind === "artifact_version" || entry.record_kind === "lifecycle_event")
      .filter((entry) => validityObservation(entry)[0] === delta.artifact_version_id).map((entry) => entry.citation_id));
    if (!setEquals(new Set(delta.timeline_citation_ids), exactTimeline)) fail(evidencePath, "validity timeline must include every cited subject observation");
    const beforeEligible = timeline.filter((entry) => compareInstant(entry.effective_at, beforeAsOf) <= 0).sort((a, b) => compareCitationsByObservation(a, b, true));
    const afterEligible = timeline.filter((entry) => compareInstant(entry.effective_at, afterAsOf) <= 0).sort((a, b) => compareCitationsByObservation(a, b, true));
    const beforeObservation = validityObservation(before);
    const afterObservation = validityObservation(after);
    if (!(beforeObservation[0] === delta.artifact_version_id && afterObservation[0] === delta.artifact_version_id
      && beforeObservation[1] === delta.before_state && afterObservation[1] === delta.after_state
      && beforeObservation[2] === delta.considered_valid_before && afterObservation[2] === delta.considered_valid_after
      && beforeEligible[beforeEligible.length - 1]?.citation_id === before.citation_id
      && afterEligible[afterEligible.length - 1]?.citation_id === after.citation_id)) {
      fail(evidencePath, "validity evidence must be the latest complete timeline at each cut");
    }
    contextsContain(delta, delta.artifact_version_id, evidencePath);
  }

  for (const [index, delta] of decisionStatuses.entries()) {
    const evidencePath = `${path}.decision_status_changes[${index}]`;
    requireContextSide(delta, "after", evidencePath);
    const before = cite(delta.before_citation_id, ["decision_status_event"], evidencePath) as TemporalDecisionStatusCitation;
    const after = cite(delta.after_citation_id, ["decision_status_event"], evidencePath) as TemporalDecisionStatusCitation;
    const timeline = delta.timeline_citation_ids.map((id) => cite(id, ["decision_status_event"], evidencePath) as TemporalDecisionStatusCitation);
    if (timeline.some((entry) => entry.decision_id !== delta.decision_id)) fail(evidencePath, "status timeline contains another subject");
    if (new Set(timeline.map((entry) => entry.artifact_version_id)).size !== 1) fail(evidencePath, "status timeline must retain one immutable target");
    const exactTimeline = new Set(citations.filter((entry): entry is TemporalDecisionStatusCitation => entry.record_kind === "decision_status_event" && entry.decision_id === delta.decision_id).map((entry) => entry.citation_id));
    if (!setEquals(new Set(delta.timeline_citation_ids), exactTimeline)) fail(evidencePath, "status timeline must include every cited subject observation");
    const beforeEligible = timeline.filter((entry) => compareInstant(entry.effective_at, beforeAsOf) <= 0).sort((a, b) => compareCitationsByObservation(a, b));
    const afterEligible = timeline.filter((entry) => compareInstant(entry.effective_at, afterAsOf) <= 0).sort((a, b) => compareCitationsByObservation(a, b));
    if (!(before.decision_id === delta.decision_id && after.decision_id === delta.decision_id && before.status === delta.before_status && after.status === delta.after_status
      && beforeEligible[beforeEligible.length - 1]?.citation_id === before.citation_id
      && afterEligible[afterEligible.length - 1]?.citation_id === after.citation_id)) {
      fail(evidencePath, "decision-status evidence must be latest at each cut");
    }
    contextsContain(delta, before.artifact_version_id, evidencePath);
    contextsContain(delta, after.artifact_version_id, evidencePath);
  }

  for (const [index, delta] of evidenceAdded.entries()) {
    const evidencePath = `${path}.evidence_membership.added[${index}]`;
    requireContextSide(delta, "after", evidencePath);
    const evidence = cite(delta.evidence_citation_id, ["decision_evidence"], evidencePath) as TemporalDecisionEvidenceCitation;
    if (!(evidence.decision_id === delta.decision_id && evidence.artifact_version_id === delta.artifact_version_id && evidence.ordinal === delta.ordinal
      && compareInstant(beforeAsOf, evidence.effective_at) < 0 && compareInstant(evidence.effective_at, afterAsOf) <= 0)) {
      fail(evidencePath, "addition citation does not prove evidence membership");
    }
    contextsContain(delta, delta.artifact_version_id, evidencePath);
  }

  for (const [index, delta] of evidenceRemoved.entries()) {
    const evidencePath = `${path}.evidence_membership.removed[${index}]`;
    requireContextSide(delta, "before", evidencePath);
    const prior = cite(delta.prior_evidence_citation_id, ["decision_evidence"], evidencePath) as TemporalDecisionEvidenceCitation;
    const retirement = cite(delta.retirement_citation_id, ["topology_retirement"], evidencePath) as TemporalTopologyRetirementCitation;
    if (!(prior.decision_id === delta.decision_id && prior.artifact_version_id === delta.artifact_version_id && prior.ordinal === delta.ordinal
      && compareInstant(prior.effective_at, beforeAsOf) <= 0 && retirement.retired_entity_kind === "decision_evidence"
      && retirement.retired_entity_id === delta.decision_id && retirement.retired_entity_ordinal === delta.ordinal
      && retirement.retirement_type === delta.retirement_action && compareInstant(beforeAsOf, retirement.effective_at) < 0
      && compareInstant(retirement.effective_at, afterAsOf) <= 0)) fail(evidencePath, "removal requires exact typed retirement evidence");
    contextsContain(delta, delta.artifact_version_id, evidencePath);
  }

  if (!setEquals(referencedCitations, new Set(citationsById.keys()))) fail(`${path}.citations`, "must exactly equal path/delta-referenced evidence");
  if (citations.some((entry) => compareInstant(entry.effective_at, afterAsOf) > 0)) fail(`${path}.citations`, "effective_at exceeds after_as_of");
  if (citations.some((entry) => compareInstant(entry.recorded_at, recordedAsOf) > 0)) fail(`${path}.citations`, "recorded_at exceeds recorded_as_of");

  const document = value as TemporalCompareDocument;
  validateVisibleRecordBudget(document, deltas);
  return document;
}

function parseResult(value: unknown, path: string): TemporalCompareResult {
  const item = exactRecord(value, path, ["document", "semantic_identity"]);
  validateDocument(item.document, `${path}.document`);
  const identity = exactRecord(item.semantic_identity, `${path}.semantic_identity`, ["digest", "canonicalizer", "canonicalizer_version", "byte_length", "media_type"]);
  digest(identity.digest, `${path}.semantic_identity.digest`);
  literal(identity.canonicalizer, `${path}.semantic_identity.canonicalizer`, ["rfc8785"]);
  literal(identity.canonicalizer_version, `${path}.semantic_identity.canonicalizer_version`, ["1"]);
  integer(identity.byte_length, `${path}.semantic_identity.byte_length`, 1, TEMPORAL_COMPARE_MAX_DOCUMENT_CANONICAL_BYTES);
  literal(identity.media_type, `${path}.semantic_identity.media_type`, [TEMPORAL_COMPARE_JSON_MEDIA_TYPE]);
  return value as TemporalCompareResult;
}

export function parseTemporalCompareEnvelope(value: unknown): TemporalCompareEnvelope {
  admitJsonValue(value, "$", TEMPORAL_COMPARE_MAX_ENVELOPE_CANONICAL_BYTES);
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  uuid(item.request_id, "$.request_id");
  parseResult(item.data, "$.data");
  if (item.page !== null) fail("$.page", "expected null for a non-paginated comparison");
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [TEMPORAL_COMPARE_CONTRACT_VERSION]);
  const migration = text(provenance.schema_migration, "$.provenance.schema_migration", 14);
  if (!/^\d{14}$/.test(migration) || migration < TEMPORAL_COMPARE_MIN_SCHEMA_MIGRATION) {
    fail("$.provenance.schema_migration", `expected a migration marker at or after ${TEMPORAL_COMPARE_MIN_SCHEMA_MIGRATION}`);
  }
  if (boundedArray(item.warnings, "$.warnings", 0).length !== 0) fail("$.warnings", "expected no transport warnings");
  return value as TemporalCompareEnvelope;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (typeof signal.throwIfAborted === "function") signal.throwIfAborted();
  throw new DOMException("Temporal comparison verification was aborted.", "AbortError");
}

/** Verify path identities and the exact RFC 8785 identity before UI admission. */
export async function verifyTemporalCompareIdentities(envelope: TemporalCompareEnvelope, options: { signal?: AbortSignal } = {}): Promise<void> {
  const document = envelope.data.document;
  admitJsonValue(document, "$.data.document", TEMPORAL_COMPARE_MAX_DOCUMENT_CANONICAL_BYTES);
  let nextPath = 0;
  const workers = Array.from({ length: Math.min(8, document.paths.length) }, async () => {
    while (nextPath < document.paths.length) {
      throwIfAborted(options.signal);
      const index = nextPath++;
      const { path_id: expected, ...payload } = document.paths[index]!;
      if (await sha256(payload) !== expected) fail(`$.data.document.paths[${index}].path_id`, "must equal SHA-256 of RFC 8785 path payload");
    }
  });
  await Promise.all(workers);
  throwIfAborted(options.signal);
  const canonical = canonicalJson(document);
  const bytes = new TextEncoder().encode(canonical);
  if (bytes.byteLength !== envelope.data.semantic_identity.byte_length) {
    fail("$.data.semantic_identity.byte_length", "must equal the RFC 8785 document byte length");
  }
  if (await sha256(document) !== envelope.data.semantic_identity.digest) {
    fail("$.data.semantic_identity.digest", "must equal SHA-256 of the RFC 8785 document");
  }
  throwIfAborted(options.signal);
}

function sameRoots(left: TemporalCompareRoot[], right: TemporalCompareRoot[]): boolean {
  return left.length === right.length && left.every((entry, index) => entry.entity_kind === right[index]?.entity_kind && entry.id === right[index]?.id);
}

function validateResponseRequestBinding(envelope: TemporalCompareEnvelope, request: TemporalCompareRequest): void {
  const document = envelope.data.document;
  if (document.project_id !== request.project_id) fail("$.data.document.project_id", "must equal request.project_id");
  if (!sameRoots(document.scope.root_refs, request.root_refs)) fail("$.data.document.scope.root_refs", "must equal the exact requested roots");
  if (compareInstant(document.before_as_of, request.before_as_of) !== 0) fail("$.data.document.before_as_of", "must equal request.before_as_of");
  if (compareInstant(document.after_as_of, request.after_as_of) !== 0) fail("$.data.document.after_as_of", "must equal request.after_as_of");
  if (request.recorded_as_of !== undefined && request.recorded_as_of !== null && document.recorded_as_of !== request.recorded_as_of) {
    fail("$.data.document.recorded_as_of", "must equal request.recorded_as_of");
  }
  if (request.snapshot_token !== undefined && request.snapshot_token !== null && document.snapshot_token !== request.snapshot_token) {
    fail("$.data.document.snapshot_token", "must equal request.snapshot_token");
  }
  const direction = request.direction ?? "both";
  if (document.scope.direction !== direction) fail("$.data.document.scope.direction", "must equal the requested/defaulted direction");
  const expectedLimits = {
    max_depth: request.max_depth ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_depth,
    max_records: request.max_records ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_records,
    max_paths: request.max_paths ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_paths,
    max_path_steps: request.max_path_steps ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_path_steps,
  };
  for (const key of Object.keys(expectedLimits) as Array<keyof typeof expectedLimits>) {
    if (document.scope.limits[key] !== expectedLimits[key]) fail(`$.data.document.scope.limits.${key}`, "must equal the requested/defaulted limit");
  }
}

export async function fetchTemporalCompare(request: TemporalCompareRequest, options: { signal?: AbortSignal } = {}): Promise<TemporalCompareEnvelope> {
  const normalized = normalizeTemporalCompareRequest(request);
  const value = await apiClient<unknown>(TEMPORAL_COMPARE_PATH, { method: "POST", body: normalized, signal: options.signal });
  const envelope = parseTemporalCompareEnvelope(value);
  validateResponseRequestBinding(envelope, normalized);
  await verifyTemporalCompareIdentities(envelope, options);
  return envelope;
}

function exportFilename(response: Response, format: TemporalCompareExportFormat): string {
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"\\/]+)"/i.exec(disposition);
  return match?.[1] ?? `thread-temporal-compare.${format}`;
}

async function errorDetail(response: Response): Promise<string> {
  try {
    const value = await response.clone().json() as { error?: { message?: unknown }; detail?: unknown };
    if (typeof value.error?.message === "string") return value.error.message;
    if (typeof value.detail === "string") return value.detail;
  } catch {
    // Export proxies may return a non-JSON error body.
  }
  return response.text().catch(() => "Unknown error");
}

export async function exportTemporalCompare(
  request: TemporalCompareExportRequest,
  format: TemporalCompareExportFormat,
  options: { signal?: AbortSignal } = {},
): Promise<TemporalCompareExport> {
  validateTemporalCompareExportRequest(request);
  const normalized: TemporalCompareExportRequest = {
    ...request,
    before_as_of: normalizeInstantToUtcMicroseconds(request.before_as_of),
    after_as_of: normalizeInstantToUtcMicroseconds(request.after_as_of),
    project_id: request.project_id.toLowerCase(),
    root_refs: request.root_refs.map((entry) => ({ entity_kind: "artifact_version", id: entry.id.toLowerCase() })),
  };
  literal(format, "format", ["json", "html"]);
  const token = await getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const apiBase = import.meta.env.VITE_API_URL ?? "/api";
  const response = await fetch(`${apiBase}${TEMPORAL_COMPARE_EXPORT_PATH}?format=${format}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(normalized),
    signal: options.signal,
  });
  if (response.status === 409) throw new TemporalCompareChangedError();
  if (!response.ok) throw new Error(`API ${response.status}: ${await errorDetail(response)}`);
  const expectedMediaType = format === "json" ? TEMPORAL_COMPARE_JSON_MEDIA_TYPE : "text/html";
  const mediaType = response.headers.get("Content-Type") ?? "";
  if (!hasExactMediaType(mediaType, expectedMediaType)) fail("response.Content-Type", `expected ${expectedMediaType}`);
  const responseDigest = response.headers.get("X-Thread-Compare-Digest");
  if (responseDigest !== normalized.expected_semantic_digest) fail("response.X-Thread-Compare-Digest", "must match the displayed semantic digest");
  let bytes: Uint8Array<ArrayBuffer>;
  try {
    bytes = await readBoundedResponseBytes(
      response,
      format === "json" ? TEMPORAL_COMPARE_MAX_DOCUMENT_CANONICAL_BYTES : TEMPORAL_COMPARE_MAX_HTML_BYTES,
    );
    if (format === "json") {
      await verifyJsonExportBytes(bytes, normalized.expected_semantic_digest);
    } else {
      verifyOfflineHtmlBytes(bytes, [
        "<h1>Thread temporal lineage comparison</h1>",
        normalized.expected_semantic_digest,
        normalized.project_id,
        normalized.before_as_of,
        normalized.after_as_of,
        normalized.recorded_as_of,
        normalized.snapshot_token,
      ]);
    }
  } catch (error) {
    if (error instanceof ExportIntegrityError) fail("response.body", error.message);
    throw error;
  }
  return {
    blob: exportBlob(bytes, mediaType),
    filename: exportFilename(response, format),
    mediaType,
    semanticDigest: responseDigest,
  };
}
